import { skipToken } from "@tanstack/react-query";
import { trpc, type RouterInputs } from "../api/trpc";

export interface UseBooksParams {
  page?: number;
  limit?: number;
  categoryIds?: string[];
  sort?:
    | "relevance"
    | "title"
    | "title_asc"
    | "title_desc"
    | "author"
    | "author_asc"
    | "author_desc"
    | "date"
    | "date_asc"
    | "date_desc"
    | "rating"
    | "rating_asc"
    | "rating_desc"
    | "popular";
  isNewRelease?: boolean;
  minViewCount?: number;
}

/**
 * Hook for fetching paginated books from the API
 */
export const useBooks = (params: UseBooksParams = {}) => {
  const { page = 1, limit = 12, categoryIds, sort, isNewRelease, minViewCount } = params;

  return trpc.books.getBooks.useQuery(
    {
      page,
      limit,
      categoryIds,
      sort,
      isNewRelease,
      minViewCount,
    },
    {
      keepPreviousData: true, // Keep previous data while loading new pages
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  );
};

/**
 * Hook for fetching a single book by ID
 */
export const useBook = (id: string) => {
  return trpc.books.getBook.useQuery(
    { id },
    {
      enabled: !!id, // Only run query if ID is provided
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 15 * 60 * 1000, // 15 minutes
    },
  );
};

/**
 * Characters of one book source: omitted version is the baseline, a
 * conversation pins its own. `undefined` waits until the source is known.
 */
export const useBookCharacters = (
  source: RouterInputs["books"]["getBookCharacters"] | undefined,
) => {
  return trpc.books.getBookCharacters.useQuery(source ?? skipToken, {
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching categories from the books router
 */
export const useBooksCategories = (includeBookCount = true) => {
  return trpc.books.getCategories.useQuery(
    { includeBookCount },
    {
      staleTime: 15 * 60 * 1000, // 15 minutes - categories change less frequently
      cacheTime: 30 * 60 * 1000, // 30 minutes
    },
  );
};
